import { ALLOWED_PRIORITIZABLE_TYPES, ITEM_OWNERSHIP, ORGANISATION_OWNERSHIP_TYPES, } from 'tidee-life-config';
import bcrypt from 'bcryptjs';
const findItemOwnershipKey = ({ itemOwnership, ownershipId, }) => Object.keys(itemOwnership).find((id) => id === ownershipId);
const findOrganisationOwnershipKey = ({ organisationOwnershipTypes, ownershipId, }) => Object.keys(organisationOwnershipTypes).find((id) => id === ownershipId);
export const genPasswordHash = (pass) => {
    const salt = bcrypt.genSaltSync(10);
    return bcrypt.hashSync(pass, salt);
};
export const genRandString = (length) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
        counter += 1;
    }
    return result;
};
// UUID v4 backed by the platform's WebCrypto randomUUID.
// Available in Node ≥19 (globalThis.crypto), all modern browsers,
// and React Native ≥0.72 (Hermes). Pre-fix this was hand-rolled
// with Math.random() — V8's xorshift128+ PRNG state is recoverable
// from ~5 outputs, so any credential-shaped value generated
// through here would be predictable if a handful of prior UUIDs
// leaked. The audit flagged this as dead credential material
// today (share invite hashes are claimed via email verification,
// not the hash itself), but a copy-paste trap for anything that
// starts treating createUUID output as a secret. The fallback
// path exists only for legacy runtimes without WebCrypto — same
// insecure behaviour as pre-fix, so no regression there either.
export const createUUID = (addDash = false) => {
    const webCrypto = globalThis.crypto;
    if (webCrypto?.randomUUID) {
        const uuid = webCrypto.randomUUID();
        return addDash ? uuid : uuid.replace(/-/g, '');
    }
    // Fallback for environments without WebCrypto. NOT secure —
    // don't treat the output as a bearer credential.
    const s = [];
    const hexDigits = '0123456789abcdef';
    const dash = addDash ? '-' : '';
    for (let i = 0; i < 36; i += 1) {
        s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = '4';
    s[19] = hexDigits.substr((s[19] && 0x3) || 0x8, 1);
    s[8] = dash;
    s[13] = dash;
    s[18] = dash;
    s[23] = dash;
    return s.join('');
};
export const validateEmail = (email = '') => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return (email !== '' &&
        typeof email === 'string' &&
        re.test(String(email).toLowerCase()));
};
export const validateName = (name = '') => name !== '' && typeof name === 'string' && name.trim().length > 0;
export const validatePassword = (password = '') => password !== '' && typeof password === 'string' && password.length >= 8;
export const calcPriority = (priorityBefore, priorityAfter) => {
    let priority;
    // First item added to the priority list.
    if (typeof priorityBefore === 'undefined' &&
        typeof priorityAfter === 'undefined') {
        priority = 16384;
    }
    else if (typeof priorityBefore !== 'undefined' &&
        typeof priorityAfter === 'undefined') {
        // Item moved/added to the end of the priority list.
        priority = priorityBefore + 16384;
    }
    else if (typeof priorityBefore === 'undefined' &&
        typeof priorityAfter !== 'undefined') {
        // Item moved/added to the beginning of the priority list.
        priority = priorityAfter / 2;
        // priority = priorityAfter - 16384;
    }
    else {
        // Move item.
        // @ts-ignore
        priority = (priorityBefore + priorityAfter) / 2;
    }
    return priority;
};
export const promiseWithTimeout = (millis, promise) => {
    const timeout = new Promise((resolve, reject) => setTimeout(() => reject(Error(`Timed out after ${millis} ms.`)), millis));
    return Promise.race([promise, timeout]);
};
export const getAddToUnprioritized = (itemType) => itemType === 'todoBox';
export const canItemBePrioritized = (parentAutoAddToUnprioritized, itemType) => !!(parentAutoAddToUnprioritized &&
    ALLOWED_PRIORITIZABLE_TYPES.includes(itemType));
export const mongoLocaleConverter = (locale) => {
    /* eslint-disable indent */
    switch (locale) {
        case 'en':
            return 'en';
        case 'en-US':
            return 'en';
        case 'fr':
            return 'fr';
        default:
            return 'en';
    }
    /* eslint-enable indent */
};
export const reactIntlLocaleConverter = (locale) => {
    /* eslint-disable indent */
    switch (locale) {
        case 'en':
            return 'en-US';
        case 'en-US':
            return 'en-US';
        case 'fr':
            return 'fr';
        default:
            return 'en-US';
    }
    /* eslint-enable indent */
};
export const dayjsLocaleConverter = (locale) => {
    switch (locale) {
        case 'en':
            return 'en';
        case 'en-US':
            return 'en';
        case 'fr':
            return 'fr';
        default:
            return 'en';
    }
};
export const datefnsLocaleConverter = (locale) => {
    /* eslint-disable indent */
    switch (locale) {
        case 'en':
            return 'en-US';
        case 'en-US':
            return 'en-US';
        case 'fr':
            return 'fr';
        default:
            return 'en-US';
    }
    /* eslint-enable indent */
};
// Maps the user-facing locale enum to a BCP-47 tag accepted by the Web Speech
// API's SpeechRecognition.lang. Keep this aligned with the locales offered in
// the profile language picker — every supported app locale should resolve to a
// region-specific tag here so dictation isn't silently stuck on en-US.
export const speechRecognitionLocaleConverter = (locale) => {
    /* eslint-disable indent */
    switch (locale) {
        case 'en':
            return 'en-US';
        case 'en-US':
            return 'en-US';
        case 'fr':
            return 'fr-FR';
        default:
            return 'en-US';
    }
    /* eslint-enable indent */
};
export const getReminderEventInfo = ({ beforeAfterOn, linkedDirectlyToTargetDate, numberOf, repeat, targetDateText, unit, }) => {
    if (!linkedDirectlyToTargetDate) {
        return `${repeat ? 'Every ' : ''}${numberOf} ${unit} ${beforeAfterOn} ${targetDateText}`;
    }
    else {
        return '';
    }
};
export const ownershipCanViewItem = ({ ownershipId, }) => {
    const ownershipKey = findItemOwnershipKey({
        itemOwnership: ITEM_OWNERSHIP,
        ownershipId,
    });
    return (!!ownershipKey && ITEM_OWNERSHIP[ownershipKey].capabilities.includes(6));
};
export const ownershipCanArchiveItem = ({ ownershipId, }) => {
    const ownershipKey = findItemOwnershipKey({
        itemOwnership: ITEM_OWNERSHIP,
        ownershipId,
    });
    return (!!ownershipKey && ITEM_OWNERSHIP[ownershipKey].capabilities.includes(3));
};
export const ownershipCanEditItem = ({ ownershipId, }) => {
    const ownershipKey = findItemOwnershipKey({
        itemOwnership: ITEM_OWNERSHIP,
        ownershipId,
    });
    return (!!ownershipKey && ITEM_OWNERSHIP[ownershipKey].capabilities.includes(2));
};
export const ownershipCanDeleteOrganisation = ({ ownershipId }) => {
    const ownershipKey = findOrganisationOwnershipKey({
        organisationOwnershipTypes: ORGANISATION_OWNERSHIP_TYPES,
        ownershipId,
    });
    return (!!ownershipKey &&
        ORGANISATION_OWNERSHIP_TYPES[ownershipKey].capabilities.includes(5));
};
export const ownershipCanEditOrganisation = ({ ownershipId }) => {
    const ownershipKey = findOrganisationOwnershipKey({
        organisationOwnershipTypes: ORGANISATION_OWNERSHIP_TYPES,
        ownershipId,
    });
    return (!!ownershipKey &&
        ORGANISATION_OWNERSHIP_TYPES[ownershipKey].capabilities.includes(1));
};
export const ownershipCanInviteToOrganisation = ({ ownershipId }) => {
    const ownershipKey = findOrganisationOwnershipKey({
        organisationOwnershipTypes: ORGANISATION_OWNERSHIP_TYPES,
        ownershipId,
    });
    return (!!ownershipKey &&
        ORGANISATION_OWNERSHIP_TYPES[ownershipKey].capabilities.includes(2));
};
export const ownershipCanUpdateMemberOfOrganisation = ({ ownershipId }) => {
    const ownershipKey = findOrganisationOwnershipKey({
        organisationOwnershipTypes: ORGANISATION_OWNERSHIP_TYPES,
        ownershipId,
    });
    return (!!ownershipKey &&
        ORGANISATION_OWNERSHIP_TYPES[ownershipKey].capabilities.includes(4));
};
export const ownershipIsAdmin = ({ ownershipId, }) => {
    return ownershipId === ORGANISATION_OWNERSHIP_TYPES.admin.id;
};
export const createQueryParams = (params) => {
    return Object.keys(params)
        .filter((k) => params[k])
        .map((k) => `${k}=${encodeURIComponent(params[k])}`)
        .join('&');
};
// Case- and accent-insensitive form for substring matching. Decomposing to
// NFD then stripping the combining marks means "ecole" finds "École" — a
// plain toLowerCase() wouldn't, and a French user typing without accents is
// the common case rather than the exotic one.
export const normalizeForSearch = (value = '') => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
// Substring filter for box pickers. An empty term returns everything, so
// callers can pass the raw input straight through.
//
// Matches a box's own title OR its parent's, so searching a container's name
// surfaces what sits inside it — someone who remembers the box lives in
// "Holiday" but not that it is called "Flights" can still find it. The parent
// is resolved from the same list, so a parent outside `boxes` simply
// contributes no match.
export const filterBoxesByTitle = (boxes, term) => {
    const needle = normalizeForSearch(term);
    if (!needle)
        return boxes;
    const titleByShareId = new Map(boxes.map((box) => [
        box.itemShareId,
        normalizeForSearch(box.title ?? ''),
    ]));
    return boxes.filter((box) => {
        if (normalizeForSearch(box.title ?? '').includes(needle))
            return true;
        const parentTitle = box.parentItemShareId
            ? titleByShareId.get(box.parentItemShareId)
            : undefined;
        return !!parentTitle && parentTitle.includes(needle);
    });
};
// Ancestor titles for a box, outermost first, excluding the box itself.
// Search results are a flat list, so without the path "Admin" under Work and
// "Admin" under Personal are indistinguishable.
//
// The depth cap mirrors assertNotMovingIntoOwnSubtree on the server: a cyclic
// chain must not spin here either. On hitting it we return what we have — a
// truncated breadcrumb is a display nuisance, not a correctness problem.
export const getBoxPathTitles = ({ boxesByShareId, itemShareId, }) => {
    const titles = [];
    const seen = new Set([itemShareId]);
    let currentId = boxesByShareId[itemShareId]?.parentItemShareId;
    while (currentId && !seen.has(currentId) && titles.length < 100) {
        seen.add(currentId);
        const parent = boxesByShareId[currentId];
        if (!parent)
            break;
        titles.unshift(parent.title ?? '');
        currentId = parent.parentItemShareId;
    }
    return titles;
};
// A box's own id plus every descendant's. Used to keep a box out of its own
// subtree in destination pickers — the server refuses such a move
// (assertNotMovingIntoOwnSubtree), so offering it can only waste a click.
export const getSubtreeShareIds = ({ boxes, rootItemShareId, }) => {
    const childrenByParent = new Map();
    boxes.forEach((box) => {
        if (!box.parentItemShareId)
            return;
        const siblings = childrenByParent.get(box.parentItemShareId) ?? [];
        siblings.push(box.itemShareId);
        childrenByParent.set(box.parentItemShareId, siblings);
    });
    const collected = new Set();
    const queue = [rootItemShareId];
    while (queue.length) {
        const id = queue.shift();
        // `collected` doubles as the visited set, so a cyclic chain in the
        // source data terminates instead of looping.
        if (collected.has(id))
            continue;
        collected.add(id);
        (childrenByParent.get(id) ?? []).forEach((childId) => queue.push(childId));
    }
    return [...collected];
};
// Move rejections the server raises with a specific, actionable reason.
// Both apps translate these against the shared lang bundles.
export const MOVE_ERROR_MESSAGE_KEYS = [
    'error.cannot.move.item.into.itself',
    'error.cannot.move.item.into.own.descendant',
    'error.cross.org.move.blocked',
];
export const GENERIC_MOVE_ERROR_MESSAGE_KEY = 'error.modal.item.save.error';
// Maps a failed move response's `message` to a translatable key. Anything
// unrecognised falls back to the generic save error — a raw server string
// or an unmapped code must never reach the user.
export const resolveMoveErrorKey = (message) => MOVE_ERROR_MESSAGE_KEYS.find((key) => key === message) ??
    GENERIC_MOVE_ERROR_MESSAGE_KEY;
// Collator for user-facing A–Z lists. `numeric` so "Box 2" sorts before
// "Box 10", `sensitivity: 'base'` so case and accents don't split
// otherwise-adjacent names. Use this rather than a bare `localeCompare()`,
// which ignores the user's locale and orders accented titles wrongly.
// Exposed for lists that need a tiebreak (pinned-first, etc.) and so can't
// use sortByTitle.
export const createNameCollator = (locale) => new Intl.Collator(locale, {
    sensitivity: 'base',
    numeric: true,
});
// How long a parent-box name may run in the organiser lists before it is
// cut short. The name is a breadcrumb there, not the content — past ~30
// characters it starts crowding out the item title it belongs to.
export const PARENT_NAME_MAX_LENGTH = 30;
// Shortens a name for display, ellipsis included in the budget: the
// returned string is never longer than maxLength. Shared so the organiser
// lists cut at the same point on web and mobile.
export const truncateName = (name, maxLength = PARENT_NAME_MAX_LENGTH) => {
    const value = name ?? '';
    if (value.length <= maxLength)
        return value;
    return `${value.slice(0, maxLength - 1).trimEnd()}…`;
};
// Locale-aware A–Z sort on `title`. Returns a new array — callers often hold
// the source list in state/props, which must not be mutated in place.
export const sortByTitle = (items, locale) => {
    const collator = createNameCollator(locale);
    return [...items].sort((a, b) => collator.compare(a.title ?? '', b.title ?? ''));
};
//# sourceMappingURL=index.js.map